import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Edit } from 'lucide-react';

// Cancellation Fee Button Component
function CancellationFeeButton({ booking, formatCurrency }) {
  const [showDialog, setShowDialog] = useState(false);
  const [feeAmount, setFeeAmount] = useState('');
  const [reason, setReason] = useState('Late cancellation');
  const [loading, setLoading] = useState(false);
  const [calculatedFee, setCalculatedFee] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  const calculateFee = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/bookings/${booking.id}/calculate-cancellation-fee`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCalculatedFee(data);
      setFeeAmount(data.recommended_fee.toString());
    } catch (error) {
      console.error('Error calculating fee:', error);
    }
  };

  const handleCharge = async () => {
    const amount = parseFloat(feeAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (!window.confirm(`Charge cancellation fee of ${formatCurrency(amount)} for: ${reason}?`)) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // Use XMLHttpRequest to avoid Response.clone issues
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/api/admin/bookings/${booking.id}/charge-cancellation-fee`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            resolve({ success: false, error: xhr.responseText || 'Unknown error' });
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(JSON.stringify({
          amount: amount,
          reason: reason,
          send_notification_email: true
        }));
      });

      if (result.success) {
        alert(`✅ Cancellation fee charged successfully!\n\nAmount: ${formatCurrency(amount)}\nOrder ID: ${result.order_id}`);
        setShowDialog(false);
        window.location.reload();
      } else {
        alert(`❌ Failed to charge: ${result.error || result.message || result.detail || 'Unknown error'}`);
      }
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const maxChargeable = booking.payment_option === 'pay_on_arrival' 
    ? booking.total_amount - (booking.cancellation_fee_charged || 0)
    : (booking.remaining_amount || 0) - (booking.cancellation_fee_charged || 0);

  return (
    <>
      <Button
        type="button"
        variant="destructive"
        size="sm"
        className="w-full"
        onClick={() => {
          setShowDialog(true);
          calculateFee();
        }}
        data-testid="charge-cancellation-fee-btn"
      >
        💳 Charge Cancellation Fee
      </Button>
      <p className="text-xs text-gray-500 mt-1 text-center">
        Max: {formatCurrency(maxChargeable)} • Card on file
      </p>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Charge Cancellation Fee</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Booking Info */}
            <div className="bg-gray-50 p-3 rounded-lg text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Booking:</span>
                <span className="font-medium">{booking.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Guest:</span>
                <span className="font-medium">{booking.guest_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Amount:</span>
                <span className="font-medium">{formatCurrency(booking.total_amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Deposit Paid:</span>
                <span className="font-medium">{formatCurrency(booking.deposit_amount || 0)}</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-gray-600">Max Chargeable:</span>
                <span className="font-bold text-orange-600">{formatCurrency(maxChargeable)}</span>
              </div>
            </div>

            {/* Policy Recommendation */}
            {calculatedFee && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <div className="font-medium text-blue-800 mb-2">Policy Recommendation</div>
                <div className="text-blue-700">
                  <div>Hours until check-in: {calculatedFee.hours_until_checkin}h</div>
                  {calculatedFee.policy_tier && (
                    <div className="mt-1 text-xs">
                      {calculatedFee.policy_tier.description}
                    </div>
                  )}
                  <div className="font-bold mt-2">
                    Recommended fee: {formatCurrency(calculatedFee.recommended_fee)}
                  </div>
                </div>
              </div>
            )}

            {/* Fee Amount */}
            <div>
              <Label htmlFor="feeAmount">Amount to Charge (€)</Label>
              <Input
                id="feeAmount"
                type="number"
                step="0.01"
                min="0"
                max={maxChargeable}
                value={feeAmount}
                onChange={(e) => setFeeAmount(e.target.value)}
                placeholder="Enter amount"
              />
              {/* Quick amount buttons */}
              <div className="flex gap-2 mt-2">
                {calculatedFee && calculatedFee.nightly_rate && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFeeAmount(calculatedFee.nightly_rate.toString())}
                  >
                    1 Night ({formatCurrency(calculatedFee.nightly_rate)})
                  </Button>
                )}
                {calculatedFee && calculatedFee.recommended_fee > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFeeAmount(calculatedFee.recommended_fee.toString())}
                  >
                    Recommended
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFeeAmount(maxChargeable.toString())}
                >
                  Max ({formatCurrency(maxChargeable)})
                </Button>
              </div>
            </div>

            {/* Reason */}
            <div>
              <Label htmlFor="reason">Reason</Label>
              <select
                id="reason"
                className="w-full border rounded-md p-2"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                <option value="Late cancellation">Late cancellation</option>
                <option value="No-show">No-show</option>
                <option value="Cancellation within 24h">Cancellation within 24h</option>
                <option value="Cancellation within 48h">Cancellation within 48h</option>
                <option value="Policy violation">Policy violation</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="flex-1"
                onClick={handleCharge}
                disabled={loading || !feeAmount || parseFloat(feeAmount) <= 0}
              >
                {loading ? 'Charging...' : `Charge ${feeAmount ? formatCurrency(parseFloat(feeAmount)) : '€0.00'}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function BookingDialog({
  showBookingDialog,
  setShowBookingDialog,
  editingBooking,
  setEditingBooking,
  bookingViewMode,
  setBookingViewMode,
  bookingForm,
  setBookingForm,
  bookingRoomTypeFilter,
  setBookingRoomTypeFilter,
  resetBookingForm,
  handleCreateBooking,
  handleUpdateBooking,
  handleDeleteBooking,
  rooms,
  roomTypes,
  cars,
  currentBooking,
  getRoomTypeName,
  getCarTypeName,
  formatDate,
  formatCurrency
}) {
  return (
    <Dialog open={showBookingDialog} onOpenChange={(open) => {
      setShowBookingDialog(open);
      if (!open) {
        setEditingBooking(null);
        resetBookingForm();
      }
    }}>
      <DialogContent className={bookingViewMode ? "max-w-4xl max-h-[85vh] overflow-y-auto" : "max-w-lg max-h-[85vh] overflow-y-auto"}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>
              {bookingViewMode ? 'Booking Details' : editingBooking ? 'Edit Booking' : 'Create New Booking'}
            </DialogTitle>
            {bookingViewMode && (
              <Button 
                type="button"
                variant="outline" 
                size="sm"
                onClick={() => setBookingViewMode(false)}
                data-testid="edit-booking-mode-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </DialogHeader>
        <form onSubmit={editingBooking ? handleUpdateBooking : handleCreateBooking} className="space-y-4">
          {bookingViewMode ? (
            <div className="grid grid-cols-2 gap-4">
              {/* Room Information */}
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                <div>
                  <Label className="text-gray-600">Room</Label>
                  <p className="text-base font-semibold">
                    {rooms.find(r => r.id === bookingForm.room_id)?.name || 'N/A'} 
                    {' '}(Room {rooms.find(r => r.id === bookingForm.room_id)?.room_number || 'N/A'})
                  </p>
                  <p className="text-sm text-gray-600">
                    {getRoomTypeName(rooms.find(r => r.id === bookingForm.room_id)?.room_type_id || '')}
                  </p>
                </div>
              </div>
              
              {/* Guest Information */}
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-sm text-gray-700">Guest Information</h4>
                <div className="space-y-2">
                  <div>
                    <Label className="text-gray-600">Name</Label>
                    <p className="font-medium">{bookingForm.guest_name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Email</Label>
                    <p className="font-medium">{bookingForm.guest_email}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Phone</Label>
                    <p className="font-medium">{bookingForm.guest_phone}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Country</Label>
                    <p className="font-medium">{bookingForm.guest_country || 'Not specified'}</p>
                  </div>
                </div>
              </div>
              
              {/* Stay Details */}
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-sm text-gray-700">Stay Details</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-gray-600">Check-in</Label>
                    <p className="font-medium">{formatDate(bookingForm.check_in)}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Check-out</Label>
                    <p className="font-medium">{formatDate(bookingForm.check_out)}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-600">Number of Guests</Label>
                  <p className="font-medium">{bookingForm.num_guests} guest{bookingForm.num_guests !== 1 ? 's' : ''}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Bed Configuration</Label>
                  <p className="font-medium">
                    {bookingForm.bed_preference === 'double' ? '🛏️ Double Bed' : 
                     bookingForm.bed_preference === 'twin' ? '🛏️🛏️ Twin Beds' : 
                     'Not specified'}
                  </p>
                </div>
                {bookingForm.special_requests && (
                  <div>
                    <Label className="text-gray-600">Special Requests</Label>
                    <p className="font-medium text-sm">{bookingForm.special_requests}</p>
                  </div>
                )}
              </div>
              
              {/* Airport Transfer & Flight Details */}
              {currentBooking && currentBooking.transfer_required && (
                <div className="space-y-3 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-sm text-blue-900 flex items-center gap-2">
                    <span>✈️</span> Airport Transfer
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-gray-600">Transfer Type</Label>
                      <p className="font-medium">
                        {currentBooking.transfer_type === 'arrival' ? 'Airport → Hotel' :
                         currentBooking.transfer_type === 'departure' ? 'Hotel → Airport' :
                         currentBooking.transfer_type === 'round_trip' ? 'Round Trip' :
                         currentBooking.transfer_type || 'N/A'}
                      </p>
                    </div>
                    {currentBooking.arrival_flight_number && (
                      <div className="bg-white p-2 rounded border border-blue-100">
                        <Label className="text-gray-600">Arrival Flight</Label>
                        <p className="font-medium text-lg">{currentBooking.arrival_flight_number}</p>
                        <p className="text-sm text-gray-600">
                          Time: <span className="font-semibold">{currentBooking.arrival_flight_time || 'Not specified'}</span>
                        </p>
                      </div>
                    )}
                    {currentBooking.departure_flight_number && (
                      <div className="bg-white p-2 rounded border border-blue-100">
                        <Label className="text-gray-600">Departure Flight</Label>
                        <p className="font-medium text-lg">{currentBooking.departure_flight_number}</p>
                        <p className="text-sm text-gray-600">
                          Time: <span className="font-semibold">{currentBooking.departure_flight_time || 'Not specified'}</span>
                        </p>
                      </div>
                    )}
                    {!currentBooking.arrival_flight_number && !currentBooking.departure_flight_number && (
                      <p className="text-sm text-orange-600 italic">⚠️ Flight details not provided by guest</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Pricing Summary */}
              {currentBooking && (
                <div className="space-y-3 bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-sm text-green-900 flex items-center gap-2">
                    <span>💰</span> Pricing Summary
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-gray-600">Room Amount:</Label>
                      <p className="font-semibold">
                        {formatCurrency(currentBooking.total_amount || 0)}
                      </p>
                    </div>
                    {currentBooking.car_amount && currentBooking.car_amount > 0 && (
                      <div className="flex justify-between items-center">
                        <Label className="text-gray-600">Car Rental <span className="text-xs text-amber-600 italic">(payable on arrival)</span>:</Label>
                        <p className="font-semibold text-amber-700">
                          {formatCurrency(currentBooking.car_amount)}
                        </p>
                      </div>
                    )}
                    {currentBooking.transfer_amount && currentBooking.transfer_amount > 0 && (
                      <div className="flex justify-between items-center">
                        <Label className="text-gray-600">Airport Transfer <span className="text-xs text-sky-600 italic">(payable to driver)</span>:</Label>
                        <p className="font-semibold text-sky-700">
                          {formatCurrency(currentBooking.transfer_amount)}
                        </p>
                      </div>
                    )}
                    <div className="border-t border-green-300 pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <Label className="text-gray-700 font-bold">Total:</Label>
                        <p className="font-bold text-xl text-green-700">
                          {formatCurrency(currentBooking.total_amount || 0)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Payment Status */}
                    <div className="border-t border-green-300 pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <Label className="text-gray-700 font-semibold">Payment Status:</Label>
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                          currentBooking.payment_status === 'paid' 
                            ? 'bg-green-100 text-green-800' 
                            : currentBooking.payment_status === 'deposit_paid'
                            ? 'bg-blue-100 text-blue-800'
                            : currentBooking.payment_status === 'pending_payment'
                            ? 'bg-yellow-100 text-yellow-800'
                            : currentBooking.payment_status === 'refunded'
                            ? 'bg-purple-100 text-purple-800'
                            : currentBooking.payment_status === 'cancelled'
                            ? 'bg-gray-100 text-gray-800'
                            : currentBooking.payment_status === 'expired'
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {currentBooking.payment_status === 'paid' ? '✓ Paid' : 
                           currentBooking.payment_status === 'deposit_paid' ? '✓ Deposit Paid' :
                           currentBooking.payment_status === 'pending_payment' ? '⏳ Pay on Arrival' : 
                           currentBooking.payment_status === 'pending' ? '⏳ Awaiting Payment' :
                           currentBooking.payment_status === 'failed' ? '❌ Payment Failed' :
                           currentBooking.payment_status === 'refunded' ? '↩ Refunded' :
                           currentBooking.payment_status === 'cancelled' ? '✕ Cancelled' :
                           currentBooking.payment_status === 'expired' ? '⌛ Expired' :
                           currentBooking.payment_status || 'Unknown'}
                        </span>
                      </div>
                      {currentBooking.deposit_amount && currentBooking.deposit_amount > 0 && (
                        <div className="flex justify-between items-center mt-2 text-sm">
                          <Label className="text-gray-600">
                            {currentBooking.payment_status === 'paid' ? 'Deposit Paid:' : 'Deposit Required:'}
                          </Label>
                          <p className={`font-medium ${currentBooking.payment_status === 'paid' ? 'text-green-700' : 'text-yellow-700'}`}>
                            {formatCurrency(currentBooking.deposit_amount)}
                          </p>
                        </div>
                      )}
                      {currentBooking.remaining_amount && currentBooking.remaining_amount > 0 && (
                        <div className="flex justify-between items-center mt-1 text-sm">
                          <Label className="text-gray-600">Remaining Balance:</Label>
                          <p className="font-medium text-orange-700">
                            {formatCurrency(currentBooking.remaining_amount)}
                          </p>
                        </div>
                      )}
                      
                      {/* Charge Remaining Button - Show when there's a remaining amount and a stored card token */}
                      {currentBooking.remaining_amount && currentBooking.remaining_amount > 0 && currentBooking.nexi_contract_id && !currentBooking.remaining_charged && (
                        <div className="mt-3 pt-3 border-t border-green-300">
                          <Button
                            type="button"
                            variant="default"
                            size="sm"
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={async () => {
                              if (!window.confirm(`Are you sure you want to charge the remaining balance of ${formatCurrency(currentBooking.remaining_amount)} to the stored card?`)) {
                                return;
                              }
                              
                              try {
                                const API = process.env.REACT_APP_BACKEND_URL;
                                const token = localStorage.getItem('admin_token');
                                
                                // Use XMLHttpRequest to avoid Response.clone issues
                                const result = await new Promise((resolve, reject) => {
                                  const xhr = new XMLHttpRequest();
                                  xhr.open('POST', `${API}/api/admin/bookings/${currentBooking.id}/charge-remaining`);
                                  xhr.setRequestHeader('Content-Type', 'application/json');
                                  xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                                  xhr.onload = () => {
                                    try {
                                      resolve(JSON.parse(xhr.responseText));
                                    } catch (e) {
                                      resolve({ success: false, error: xhr.responseText || 'Unknown error' });
                                    }
                                  };
                                  xhr.onerror = () => reject(new Error('Network error'));
                                  xhr.send(JSON.stringify({ send_receipt_email: true }));
                                });
                                
                                if (result.success) {
                                  alert(`✅ Successfully charged ${formatCurrency(currentBooking.remaining_amount)}\n\nReceipt email sent: ${result.receipt_email_sent ? 'Yes' : 'No'}`);
                                  // Refresh booking data
                                  window.location.reload();
                                } else {
                                  alert(`❌ Failed to charge: ${result.error || result.message || result.detail || 'Unknown error'}`);
                                }
                              } catch (error) {
                                alert(`❌ Error: ${error.message}`);
                              }
                            }}
                            data-testid="charge-remaining-btn"
                          >
                            💳 Charge Remaining {formatCurrency(currentBooking.remaining_amount)}
                          </Button>
                          <p className="text-xs text-gray-500 mt-1 text-center">
                            Card on file • No 3DS required
                          </p>
                        </div>
                      )}
                      
                      {/* Show if remaining was already charged */}
                      {currentBooking.remaining_charged && (
                        <div className="mt-2 text-sm text-green-600">
                          ✅ Remaining balance charged on {new Date(currentBooking.remaining_charged_at).toLocaleString()}
                        </div>
                      )}
                      
                      {/* Charge Cancellation Fee Button - Show when booking has stored card and isn't fully paid */}
                      {currentBooking.nexi_contract_id && 
                       (currentBooking.payment_status === 'cancelled' || 
                        currentBooking.payment_status === 'pending' ||
                        currentBooking.payment_status === 'pending_payment' ||
                        (currentBooking.remaining_amount > 0 && !currentBooking.remaining_charged)) && (
                        <div className="mt-3 pt-3 border-t border-red-300">
                          <CancellationFeeButton 
                            booking={currentBooking} 
                            formatCurrency={formatCurrency}
                          />
                        </div>
                      )}
                      
                      {/* Show if cancellation fee was charged */}
                      {currentBooking.cancellation_fee_charged && currentBooking.cancellation_fee_charged > 0 && (
                        <div className="mt-2 text-sm text-orange-600">
                          ⚠️ Cancellation fee charged: {formatCurrency(currentBooking.cancellation_fee_charged)}
                          {currentBooking.cancellation_fee_reason && (
                            <span className="text-gray-500"> ({currentBooking.cancellation_fee_reason})</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Car Rental - Spans both columns if exists */}
              {bookingForm.car_id && (
                <div className="col-span-2 space-y-3 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-sm text-blue-900 flex items-center gap-2">
                    <span>🚗</span> Car Rental
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-600">Vehicle</Label>
                      <p className="font-medium">
                        {cars.find(c => c.id === bookingForm.car_id)?.car_number || 'N/A'} - {' '}
                        {cars.find(c => c.id === bookingForm.car_id)?.name || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {getCarTypeName(cars.find(c => c.id === bookingForm.car_id)?.car_type_id || '')}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-gray-600">Start</Label>
                        <p className="font-medium">{formatDate(bookingForm.car_start_date)}</p>
                      </div>
                      <div>
                        <Label className="text-gray-600">End</Label>
                        <p className="font-medium">{formatDate(bookingForm.car_end_date)}</p>
                      </div>
                    </div>
                  </div>
                  {bookingForm.car_start_date && bookingForm.car_end_date && (
                    <div className="pt-2 border-t border-blue-200">
                      <p className="text-sm text-gray-700">
                        {Math.ceil((new Date(bookingForm.car_end_date) - new Date(bookingForm.car_start_date)) / (1000 * 60 * 60 * 24))} days × {formatCurrency(cars.find(c => c.id === bookingForm.car_id)?.base_daily_rate || 0)} = <span className="font-bold text-blue-700">{formatCurrency((Math.ceil((new Date(bookingForm.car_end_date) - new Date(bookingForm.car_start_date)) / (1000 * 60 * 60 * 24))) * (cars.find(c => c.id === bookingForm.car_id)?.base_daily_rate || 0))}</span>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
          {/* Room Information */}
          <div>
                <Label htmlFor="booking_room_type_filter">Filter by Room Type</Label>
                <select
                  id="booking_room_type_filter"
                  value={bookingRoomTypeFilter}
                  onChange={(e) => setBookingRoomTypeFilter(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="all">All Room Types</option>
                  {roomTypes.map(type => {
                    // Simplified display names
                    let displayName = type.name;
                    if (type.name.includes('Ground Floor')) displayName = 'Ground';
                    else if (type.name.includes('Mountain View')) displayName = 'Mountain';
                    else if (type.name.includes('Sea View and Terrace')) displayName = 'Terrace';
                    else if (type.name.includes('Sea View')) displayName = 'Sea View';
                    else if (type.name.includes('Single')) displayName = 'Single';
                    
                    return (
                      <option key={type.id} value={type.id}>
                        {displayName} ({rooms.filter(r => r.room_type_id === type.id).length} available)
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <Label htmlFor="booking_room_id">Room *</Label>
                <select
                  id="booking_room_id"
                  value={bookingForm.room_id}
                  onChange={(e) => setBookingForm({ ...bookingForm, room_id: e.target.value })}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="booking-room-select"
                >
                  <option value="">Select room</option>
                  {(bookingRoomTypeFilter === 'all' 
                    ? rooms 
                    : rooms.filter(r => r.room_type_id === bookingRoomTypeFilter)
                  ).map(room => (
                    <option key={room.id} value={room.id}>
                      {room.name} (Room {room.room_number}) - {getRoomTypeName(room.room_type_id)}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Guest Information */}
              <div>
                <Label htmlFor="booking_guest_name">Guest Name *</Label>
                <Input
                  id="booking_guest_name"
                  value={bookingForm.guest_name}
                  onChange={(e) => setBookingForm({ ...bookingForm, guest_name: e.target.value })}
                  required
                  placeholder="John Doe"
                  data-testid="booking-guest-name"
                />
              </div>
              
              <div>
                <Label htmlFor="booking_guest_email">Guest Email *</Label>
                <Input
                  id="booking_guest_email"
                  type="email"
                  value={bookingForm.guest_email}
                  onChange={(e) => setBookingForm({ ...bookingForm, guest_email: e.target.value })}
                  required
                  placeholder="john@example.com"
                  data-testid="booking-guest-email"
                />
              </div>
              
              <div>
                <Label htmlFor="booking_guest_phone">Guest Phone *</Label>
                <Input
                  id="booking_guest_phone"
                  type="tel"
                  value={bookingForm.guest_phone}
                  onChange={(e) => setBookingForm({ ...bookingForm, guest_phone: e.target.value })}
                  required
                  placeholder="+1 (555) 123-4567"
                  data-testid="booking-guest-phone"
                />
              </div>
              
              <div>
                <Label htmlFor="booking_guest_country">Country</Label>
                <Input
                  id="booking_guest_country"
                  value={bookingForm.guest_country || ''}
                  onChange={(e) => setBookingForm({ ...bookingForm, guest_country: e.target.value })}
                  placeholder="e.g., Greece, Italy, Germany"
                  data-testid="booking-guest-country"
                />
              </div>
              
              {/* Stay Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="booking_check_in">Check-in Date *</Label>
                  <Input
                    id="booking_check_in"
                    type="date"
                    value={bookingForm.check_in}
                    onChange={(e) => setBookingForm({ ...bookingForm, check_in: e.target.value })}
                    required
                    data-testid="booking-check-in"
                  />
                </div>
                <div>
                  <Label htmlFor="booking_check_out">Check-out Date *</Label>
                  <Input
                    id="booking_check_out"
                    type="date"
                    value={bookingForm.check_out}
                    onChange={(e) => setBookingForm({ ...bookingForm, check_out: e.target.value })}
                    required
                    data-testid="booking-check-out"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="booking_num_guests">Number of Guests *</Label>
                <Input
                  id="booking_num_guests"
                  type="number"
                  min="1"
                  value={bookingForm.num_guests}
                  onChange={(e) => setBookingForm({ ...bookingForm, num_guests: parseInt(e.target.value) || 1 })}
                  required
                  data-testid="booking-num-guests"
                />
              </div>
              
              {/* Bed Preference */}
              <div>
                <Label className="text-base font-medium mb-2 block">Bed Configuration *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                      bookingForm.bed_preference === 'double' 
                        ? 'border-blue-600 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setBookingForm({ ...bookingForm, bed_preference: 'double' })}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="admin_bed_preference"
                        value="double"
                        checked={bookingForm.bed_preference === 'double'}
                        onChange={(e) => setBookingForm({ ...bookingForm, bed_preference: e.target.value })}
                        className="w-4 h-4"
                      />
                      <div>
                        <div className="font-semibold text-sm">🛏️ Double</div>
                        <div className="text-xs text-gray-600">One double bed</div>
                      </div>
                    </div>
                  </div>

                  <div 
                    className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                      bookingForm.bed_preference === 'twin' 
                        ? 'border-blue-600 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setBookingForm({ ...bookingForm, bed_preference: 'twin' })}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="admin_bed_preference"
                        value="twin"
                        checked={bookingForm.bed_preference === 'twin'}
                        onChange={(e) => setBookingForm({ ...bookingForm, bed_preference: e.target.value })}
                        className="w-4 h-4"
                      />
                      <div>
                        <div className="font-semibold text-sm">🛏️🛏️ Twin</div>
                        <div className="text-xs text-gray-600">Two single beds</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <Label htmlFor="booking_special_requests">Special Requests</Label>
                <Textarea
                  id="booking_special_requests"
                  value={bookingForm.special_requests}
                  onChange={(e) => setBookingForm({ ...bookingForm, special_requests: e.target.value })}
                  placeholder="Any special requirements..."
                  rows={3}
                  data-testid="booking-special-requests"
                />
              </div>
              
              {/* Payment Status - Only show when editing */}
              {editingBooking && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <span>💳</span> Payment Information
                  </h4>
                  <div>
                    <Label htmlFor="booking_payment_status">Payment Status *</Label>
                    <select
                      id="booking_payment_status"
                      value={bookingForm.payment_status || 'pending'}
                      onChange={(e) => setBookingForm({ ...bookingForm, payment_status: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="booking-payment-status"
                    >
                      <option value="pending">⏳ Awaiting Payment</option>
                      <option value="deposit_paid">✓ Deposit Paid</option>
                      <option value="pending_payment">⏳ Pay on Arrival</option>
                      <option value="paid">✓ Paid</option>
                      <option value="failed">❌ Payment Failed</option>
                      <option value="refunded">↩ Refunded</option>
                      <option value="cancelled">✕ Cancelled</option>
                      <option value="expired">⌛ Expired</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Update the payment status to reflect current payment state
                    </p>
                  </div>
                </div>
              )}
              
              {/* Car Rental Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <span>🚗</span> Add Car Rental (Optional)
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="booking_car_id">Select Car</Label>
                    <select
                      id="booking_car_id"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={bookingForm.car_id || ''}
                      onChange={(e) => {
                        const carId = e.target.value;
                        if (!carId) {
                          setBookingForm({
                            ...bookingForm,
                            car_id: null,
                            car_start_date: '',
                            car_end_date: ''
                          });
                        } else {
                          setBookingForm({
                            ...bookingForm,
                            car_id: carId,
                            car_start_date: bookingForm.check_in,
                            car_end_date: bookingForm.check_out
                          });
                        }
                      }}
                    >
                      <option value="">No car rental</option>
                      {cars.filter(car => car.status === "available").map(car => (
                        <option key={car.id} value={car.id}>
                          {car.name} - {formatCurrency(car.base_daily_rate)}/day
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {bookingForm.car_id && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="booking_car_start_date">Car Pickup Date</Label>
                        <Input
                          id="booking_car_start_date"
                          type="date"
                          value={bookingForm.car_start_date}
                          onChange={(e) => setBookingForm({ ...bookingForm, car_start_date: e.target.value })}
                          min={bookingForm.check_in}
                          max={bookingForm.check_out}
                        />
                      </div>
                      <div>
                        <Label htmlFor="booking_car_end_date">Car Return Date</Label>
                        <Input
                          id="booking_car_end_date"
                          type="date"
                          value={bookingForm.car_end_date}
                          onChange={(e) => setBookingForm({ ...bookingForm, car_end_date: e.target.value })}
                          min={bookingForm.car_start_date || bookingForm.check_in}
                          max={bookingForm.check_out}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          
          {/* Pricing Summary - Edit Mode Only */}
          {!bookingViewMode && bookingForm.room_id && (
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <span>💰</span> Pricing Summary
              </h4>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Room Rate:</span>
                  <span className="font-medium">
                    {(() => {
                      const room = rooms.find(r => r.id === bookingForm.room_id);
                      if (!room) return '—';
                      const roomType = roomTypes.find(rt => rt.id === room.room_type_id);
                      return roomType ? formatCurrency(roomType.base_price) : '—';
                    })()}/night
                  </span>
                </div>
                {bookingForm.car_id && bookingForm.car_start_date && bookingForm.car_end_date && (
                  <div className="flex justify-between text-sm text-amber-700">
                    <span>Car Rental <span className="text-xs italic">(payable on arrival)</span>:</span>
                    <span className="font-medium">
                      {formatCurrency((Math.ceil((new Date(bookingForm.car_end_date) - new Date(bookingForm.car_start_date)) / (1000 * 60 * 60 * 24))) * (cars.find(c => c.id === bookingForm.car_id)?.base_daily_rate || 0))}
                    </span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-semibold">
                    <span>Estimated Total:</span>
                    <span className="text-blue-600">
                      {(() => {
                        const room = rooms.find(r => r.id === bookingForm.room_id);
                        let roomRate = 0;
                        if (room) {
                          const roomType = roomTypes.find(rt => rt.id === room.room_type_id);
                          roomRate = roomType ? roomType.base_price : 0;
                        }
                        return formatCurrency(roomRate);
                      })()}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    * Final amount may vary based on stay duration and special rates
                  </p>
                </div>
              </div>
            </div>
          )}
          {!bookingViewMode && (
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600" data-testid="save-booking-btn">
                {editingBooking ? 'Update Booking' : 'Create Booking'}
              </Button>
              {editingBooking && (
                <Button 
                  type="button" 
                  variant="destructive" 
                  onClick={() => {
                    handleDeleteBooking(editingBooking);
                    setShowBookingDialog(false);
                  }}
                  data-testid="delete-booking-dialog-btn"
                >
                  Delete
                </Button>
              )}
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
